import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Webhook,
  Add,
  DeleteOutline,
  History,
  ContentCopy,
  ErrorOutline,
  PlayArrow,
  WarningAmber,
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';
import styles from './Webhooks.module.scss';
import apiService from '../../services/apiService';
import agentService from '../../services/agentService';
import CompactExecutionLogs from '../shared/CompactExecutionLogs';
import ExecutionLogsDrawer from '../shared/ExecutionLogsDrawer';
import {
  buildWebhookDeliverySections,
  filterLogsByDate,
  formatLogDateTime,
  getLogTimeValue,
  getWebhookDeliveryStatusMeta,
  groupLogsByDate,
} from '../../utils/executionLogUtils';
import { copyTextToClipboard } from '../../utils/clipboard';

const EVENT_TYPES = [
  { 
    id: 'agent_started', 
    label: 'Agent Started', 
    description: 'Triggered when an agent execution flow begins. Includes agent_id and session_id.',
    examplePayload: {
      event_type: 'agent_started',
      agent_id: 'agent-123',
      timestamp: '2024-01-01T10:00:00Z',
      session_id: 'session-456'
    }
  },
  { 
    id: 'agent_completed', 
    label: 'Agent Completed', 
    description: 'Triggered when an agent finishes successfully. Includes complete output/response.',
    examplePayload: {
      event_type: 'agent_completed',
      agent_id: 'agent-123',
      output: 'Agent response text',
      timestamp: '2024-01-01T10:05:00Z',
      session_id: 'session-456'
    }
  },
  { 
    id: 'agent_failed', 
    label: 'Agent Failed', 
    description: 'Triggered when an agent crashes, times out, or encounters an error.',
    examplePayload: {
      event_type: 'agent_failed',
      agent_id: 'agent-123',
      error: 'Timeout after 30 seconds',
      timestamp: '2024-01-01T10:05:00Z',
      session_id: 'session-456'
    }
  },
  { 
    id: 'tool_executed', 
    label: 'Tool Executed', 
    description: 'Triggered when an agent successfully uses a tool. Includes tool name and output.',
    examplePayload: {
      event_type: 'tool_executed',
      agent_id: 'agent-123',
      tool_name: 'search_tool',
      tool_input: { query: 'search term' },
      tool_output: { results: [] },
      timestamp: '2024-01-01T10:02:00Z'
    }
  },
  { 
    id: 'tool_failed', 
    label: 'Tool Failed', 
    description: 'Triggered when an agent fails to use a tool. Includes error details.',
    examplePayload: {
      event_type: 'tool_failed',
      agent_id: 'agent-123',
      tool_name: 'search_tool',
      error: 'API key invalid',
      timestamp: '2024-01-01T10:02:00Z'
    }
  },
];

const Webhooks = () => {
  const { isDarkMode } = useTheme();
  const [subscriptions, setSubscriptions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Only card view is supported now
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Logs state
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsSubId, setLogsSubId] = useState(null);
  const [logsSubscription, setLogsSubscription] = useState(null);
  const [logDateFilter, setLogDateFilter] = useState('all');
  const [customLogDate, setCustomLogDate] = useState('');

  // Notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Test Trigger state
  const [anchorElTestSub, setAnchorElTestSub] = useState(null);
  const [testSubscription, setTestSubscription] = useState(null);
  const [testingEvent, setTestingEvent] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [deletingSubscription, setDeletingSubscription] = useState(false);

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250',
      },
    },
    typography: {
      fontFamily: '"Poppins", "sans-serif"',
    },
  });

  const loadAgentsAll = useCallback(async () => {
    const limit = 100;
    let page = 1;
    let totalPages = 1;
    const allAgents = [];

    while (page <= totalPages) {
      const response = await agentService.getAgents(page, limit);
      const pageAgents = response?.agents || [];
      allAgents.push(...pageAgents);
      totalPages = response?.total_pages || 1;
      page += 1;
    }

    return allAgents;
  }, []);

  const agentNameMap = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => map.set(String(agent.id), agent.name || `Agent ${agent.id}`));
    return map;
  }, [agents]);

  const getSubscriptionAgentLabel = (subscription) => {
    if (!subscription?.agent_id) return 'All agents';
    return agentNameMap.get(String(subscription.agent_id)) || `Agent ${subscription.agent_id}`;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let subscriptionData = [];
      try {
        subscriptionData = await apiService.getWebhookSubscriptions();
      } catch (err) {
        console.error('Error fetching webhook subscriptions:', err);
        setError(
          err?.message?.includes('Network')
            ? 'Could not reach the webhook API. Check your connection or try again.'
            : err?.message || 'Failed to load webhook subscriptions.',
        );
        setSubscriptions([]);
        return;
      }

      let agentsData = [];
      try {
        agentsData = await loadAgentsAll();
      } catch (err) {
        console.error('Error loading agents for webhook labels:', err);
      }

      setSubscriptions(subscriptionData || []);
      setAgents(agentsData || []);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError(err?.message || 'Failed to load webhook subscriptions.');
    } finally {
      setLoading(false);
    }
  }, [loadAgentsAll]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddOpen = () => {
    setNewUrl('');
    setSelectedEvent('');
    setSelectedAgentId('');
    setOpenAddDialog(true);
  };

  const handleAddClose = () => {
    setOpenAddDialog(false);
  };


  const handleCreate = async () => {
    if (!newUrl || !selectedEvent || !selectedAgentId) return;
    
    try {
      setSubmitting(true);
      await apiService.createWebhookSubscription({
        target_url: newUrl,
        events: [selectedEvent],
        agent_id: Number(selectedAgentId),
      });
      setSnackbar({ open: true, message: 'Webhook subscription created successfully!', severity: 'success' });
      handleAddClose();
      fetchData();
    } catch (err) {
      console.error('Error creating subscription:', err);
      setSnackbar({ open: true, message: 'Failed to create subscription.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (subscription) => {
    setSubscriptionToDelete(subscription);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    if (deletingSubscription) return;
    setDeleteDialogOpen(false);
    setSubscriptionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!subscriptionToDelete?.id) return;

    try {
      setDeletingSubscription(true);
      await apiService.deleteWebhookSubscription(subscriptionToDelete.id);
      setSnackbar({ open: true, message: 'Webhook deleted successfully.', severity: 'info' });
      setDeleteDialogOpen(false);
      setSubscriptionToDelete(null);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete webhook.', severity: 'error' });
    } finally {
      setDeletingSubscription(false);
    }
  };

  const handleTestClick = (event, sub) => {
    // If only one event, trigger immediately as per user request to avoid extra clicks
    if (sub.events && sub.events.length === 1) {
      setTestSubscription(sub);
      handleTestSelect(sub.events[0], sub.id);
      return;
    }
    
    // Multiple events - show clean menu
    setAnchorElTestSub(event.currentTarget);
    setTestSubscription(sub);
  };

  const handleTestClose = () => {
    setAnchorElTestSub(null);
    setTestSubscription(null);
  };

  const handleTestSelect = async (eventType, subIdOverride = null) => {
    const subId = subIdOverride || testSubscription?.id;
    if (!subId) return;

    handleTestClose();
    setTestingEvent(true);
    try {
      await apiService.testWebhookTrigger(subId, eventType.replace(/\./g, '_'));
      setSnackbar({ 
        open: true, 
        message: `Successfully triggered test for ${getEventLabel(eventType)}. Check logs!`, 
        severity: 'success' 
      });
    } catch (err) {
      console.error('Error triggering test:', err);
      setSnackbar({ 
        open: true, 
        message: 'Failed to trigger test event.', 
        severity: 'error' 
      });
    } finally {
      setTestingEvent(false);
    }
  };

  const viewLogs = async (sub) => {
    const subscription = typeof sub === 'object' ? sub : subscriptions.find((item) => item.id === sub);
    const subId = subscription?.id ?? sub;
    setLogsSubId(subId);
    setLogsSubscription(subscription || null);
    setLogsDrawerOpen(true);
    setLogDateFilter('all');
    setCustomLogDate('');
    await loadLogs(subId);
  };

  const loadLogs = useCallback(async (subId = logsSubId) => {
    if (!subId) return;
    setLoadingLogs(true);
    try {
      const data = await apiService.getWebhookLogs(subId);
      setLogs(data || []);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [logsSubId]);

  const clearLogs = async () => {
    if (!logsSubId) return;
    try {
      await apiService.clearWebhookLogs(logsSubId);
      setLogs([]);
    } catch (err) {
      console.error('Error clearing logs:', err);
    }
  };

  const groupedLogs = useMemo(() => {
    const filtered = filterLogsByDate(logs, logDateFilter, customLogDate);
    return groupLogsByDate(filtered);
  }, [logs, logDateFilter, customLogDate]);

  const copyToClipboard = (text) => {
    copyTextToClipboard(text)
      .then(() => setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' }))
      .catch(() => setSnackbar({ open: true, message: 'Failed to copy', severity: 'error' }));
  };

  const getEventLabel = (eventId) => {
    // Normalize dot to underscore for lookup if needed
    const normalizedId = eventId.replace(/\./g, '_');
    const event = EVENT_TYPES.find(e => e.id === normalizedId);
    return event ? event.label : eventId;
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.webhooksLayout}>
          <Box className={styles.header}>
            <Box className={styles.titleSection}>
              <Typography variant="h4" component="h1">Webhook Subscriptions</Typography>
              <Typography variant="body2">Manage real-time event notifications for your external workflows</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Only card view remains, so toggle removed */}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddOpen}
                className={styles.addButton}
              >
                Add Endpoint
              </Button>
            </Box>
          </Box>

        {error && (
          <Alert severity="error" icon={<ErrorOutline />} sx={{ mb: 2, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : subscriptions.length === 0 ? (
          <Box className={styles.emptyState}>
            <Webhook className={styles.emptyIcon} />
            <Typography variant="h6">No webhooks configured</Typography>
            <Typography variant="body2">
              Start by adding a target URL to receive events like task completions or tool execution updates.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} className={styles.subscriptionGrid} sx={{ flexWrap: 'wrap' }}>
            {subscriptions.map((sub) => (
              <Grid item xs={12} md={6} lg={4} key={sub.id} sx={{ display: 'flex', flex: '1 1 auto', maxWidth: { xs: '100%', md: '50%', lg: '33.333%' } }}>
                <Card className={styles.subscriptionCard} elevation={0}>
                  <Box className={styles.cardHeader}>
                    <span className={`${styles.statusPill} ${styles.active}`}>Active</span>
                    <Box>
                      <Tooltip title="Test Webhook">
                        <IconButton 
                          size="small" 
                          className={styles.iconButton} 
                          onClick={(e) => handleTestClick(e, sub)}
                          disabled={testingEvent}
                        >
                          {testingEvent && testSubscription?.id === sub.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <PlayArrow fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    <Tooltip title="View Logs">
                      <IconButton size="small" className={styles.iconButton} onClick={() => viewLogs(sub)}>
                        <History fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Subscription">
                      <IconButton 
                        size="small" 
                        className={`${styles.iconButton} ${styles.delete}`}
                        onClick={() => handleDelete(sub)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box className={styles.cardBody}>
                  <Typography variant="caption" className={styles.urlLabel}>TARGET URL</Typography>
                  <Box className={styles.urlContainer}>
                    <Typography className={styles.urlValueMono}>{sub.target_url}</Typography>
                    <Tooltip title="Copy URL">
                      <IconButton size="small" onClick={() => copyToClipboard(sub.target_url)} sx={{ ml: 1, opacity: 0.6 }}>
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <Typography variant="caption" className={styles.eventsLabel}>AGENT SCOPE</Typography>
                  <Box className={styles.agentScopeBox}>
                    <span className={sub.agent_id ? styles.agentScopeTag : styles.allAgentsScopeTag}>
                      {getSubscriptionAgentLabel(sub)}
                    </span>
                  </Box>
                  
                  <Typography variant="caption" className={styles.eventsLabel}>EVENTS</Typography>
                  <Box className={styles.eventsList}>
                    {sub.events && sub.events.map(event => (
                      <span key={event} className={styles.eventTag}>{getEventLabel(event)}</span>
                    ))}
                  </Box>
                </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add Webhook Dialog */}
        <Dialog 
          open={openAddDialog} 
          onClose={handleAddClose}
          classes={{ paper: styles.dialogPaper }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className={styles.dialogTitle}>
            Add Webhook Endpoint
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <Box className={`${styles.formField} ${styles.dialogSection}`}>
              <span className={styles.sectionTitle}>Endpoint Configuration</span>
              <TextField
                autoFocus
                label="Target URL"
                type="url"
                fullWidth
                variant="outlined"
                placeholder="https://yourserver.com/webhooks"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                autoComplete="off"
              />
            </Box>
            
            <Box className={styles.dialogSection}>
              <span className={styles.sectionTitle}>Agent Scope</span>
              <FormControl fullWidth variant="outlined" className={styles.formField} required>
                <InputLabel id="agent-select-label">Agent</InputLabel>
                <Select
                  labelId="agent-select-label"
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  label="Agent"
                >
                  {agents.map((agent) => (
                    <MenuItem key={agent.id} value={String(agent.id)}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {agent.name || `Agent ${agent.id}`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          Only this agent will trigger the webhook
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                  {agents.length === 0 && (
                    <MenuItem disabled value="">
                      No agents available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            <Box className={styles.dialogSection}>
              <span className={styles.sectionTitle}>Event to Subscribe</span>
              <FormControl fullWidth variant="outlined" className={styles.formField}>
                <InputLabel id="event-select-label">Event Type</InputLabel>
                <Select
                  labelId="event-select-label"
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  label="Event Type"
                >
                  {EVENT_TYPES.map(event => (
                    <MenuItem key={event.id} value={event.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {event.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          {event.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {selectedEvent && (
              <Box className={styles.dialogSection}>
                <span className={styles.sectionTitle}>Example Payload</span>
                <Box sx={{ 
                  background: 'var(--bg-hover)', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: '12px', 
                  p: 2,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: 'var(--text-primary)'
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(
                      EVENT_TYPES.find(e => e.id === selectedEvent)?.examplePayload || {},
                      null,
                      2
                    )}
                  </pre>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleAddClose} sx={{ color: 'var(--text-secondary)' }}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleCreate} 
              disabled={submitting || !newUrl || !selectedEvent || !selectedAgentId}
              className={styles.addButton}
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'Create Subscription'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Test Trigger Menu */}
        <Menu
          anchorEl={anchorElTestSub}
          open={Boolean(anchorElTestSub)}
          onClose={handleTestClose}
          classes={{ paper: styles.testMenuPaper }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {EVENT_TYPES.filter(event => testSubscription?.events?.includes(event.id)).map((event) => (
            <MenuItem 
              key={event.id} 
              onClick={() => handleTestSelect(event.id)}
              className={styles.testMenuItem}
            >
              <ListItemIcon className={styles.testMenuIcon}>
                <PlayArrow fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={event.label} 
                secondary={event.description.split('.')[0] + '.'} 
                classes={{ secondary: styles.testMenuSecondary }}
              />
            </MenuItem>
          ))}
          {testSubscription?.events?.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">No events registered</Typography>
            </Box>
          )}
        </Menu>

        <ExecutionLogsDrawer
          open={logsDrawerOpen}
          onClose={() => setLogsDrawerOpen(false)}
          title="Delivery Logs"
          subtitle={logsSubscription?.target_url || 'Recent webhook delivery attempts'}
          loading={loadingLogs}
          onRefresh={() => loadLogs()}
          onClear={clearLogs}
          refreshDisabled={!logsSubId}
          clearDisabled={!logsSubId || logs.length === 0}
        >
          <CompactExecutionLogs
            groupedLogs={groupedLogs}
            loading={loadingLogs}
            emptyMessage="No logs found for this endpoint."
            filteredEmptyMessage="No logs in this range."
            showDateFilters
            logDateFilter={logDateFilter}
            onLogDateFilterChange={setLogDateFilter}
            customLogDate={customLogDate}
            onCustomLogDateChange={setCustomLogDate}
            resetKey={logsSubId}
            resolveLogId={(log, index) => log.id || `webhook-log-${index}`}
            resolveHeader={(log) => ({
              statusMeta: getWebhookDeliveryStatusMeta(log.response_status),
              source: getEventLabel(log.event_type),
              time: formatLogDateTime(getLogTimeValue(log)),
              error: log.error_message,
            })}
            resolveSections={buildWebhookDeliverySections}
          />
        </ExecutionLogsDrawer>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          classes={{ paper: styles.dialogPaper }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className={styles.dialogTitle}>Delete Webhook</DialogTitle>
          <DialogContent className={styles.deleteDialogContent}>
            <Box className={styles.warningIconWrap}>
              <WarningAmber />
            </Box>
            <Typography className={styles.deleteDialogHeading}>Delete this webhook endpoint?</Typography>
            <Typography className={styles.deleteDialogText}>
              This will permanently remove delivery to {subscriptionToDelete?.target_url || 'this endpoint'}. This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button onClick={handleCancelDelete} disabled={deletingSubscription} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={deletingSubscription}
              variant="contained"
              className={styles.dangerButton}
              startIcon={deletingSubscription ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
            >
              {deletingSubscription ? 'Deleting...' : 'Delete Webhook'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ borderRadius: '12px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default Webhooks;
